import {
  HUMAN_ACCOUNT_TYPES,
  HUMAN_ACCOUNT_TYPE_VALUES,
  OMNIWOUND_TRANSITIONAL_REALM_ROLES,
  type HumanAccountType,
} from '@omniwound/auth-contract';

export type PrincipalReadiness =
  | 'provider-ready'
  | 'staff-account-shell'
  | 'roleless'
  | 'unknown-role'
  | 'multiple-account-types'
  | 'legacy-admin'
  | 'provider-missing-practitioner';

const BUILT_IN_REALM_ROLES = new Set(['offline_access', 'uma_authorization']);

function isHumanAccountType(role: string): role is HumanAccountType {
  return (HUMAN_ACCOUNT_TYPE_VALUES as readonly string[]).includes(role);
}

function isBuiltInRealmRole(role: string): boolean {
  return BUILT_IN_REALM_ROLES.has(role) || role.startsWith('default-roles-');
}

/**
 * Mirrors the BFF's supported-principal boundary before mounting any API
 * consumers. Built-in Keycloak realm roles are ignored; product roles remain
 * exact and a principal must carry exactly one.
 */
export function principalReadiness(
  realmRoles: readonly string[],
  practitionerId: string | undefined,
): PrincipalReadiness {
  if (realmRoles.includes(OMNIWOUND_TRANSITIONAL_REALM_ROLES.ADMIN)) {
    return 'legacy-admin';
  }
  const humanAccountTypes = realmRoles.filter(isHumanAccountType);
  if (humanAccountTypes.length > 1) {
    return 'multiple-account-types';
  }
  if (humanAccountTypes.length === 0) {
    return realmRoles.some((role) => !isBuiltInRealmRole(role))
      ? 'unknown-role'
      : 'roleless';
  }
  if (humanAccountTypes[0] === HUMAN_ACCOUNT_TYPES.PROVIDER) {
    return practitionerId?.trim()
      ? 'provider-ready'
      : 'provider-missing-practitioner';
  }
  return 'staff-account-shell';
}
